import { CacheKeys, ErrorBoundary, useConfig, useSessionStorage } from '@bechtel/client-common';
import { Carousel, ShiftLog } from '@bechtel/client-dashboard-common';
import { WeldDrawerValuesProps } from '@bechtel/client-welds';
import { aboveBreakpoint, Box, Spinner, SpinnerSize, Theme, useMediaQuery } from '@bechtel/gds';
import { CommodityStats, CrewMember, PersonaType } from '@bechtel/types';
import { get } from 'lodash';
import { FC, Suspense, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { CommodityStatsSection } from '~/commodity-stats';
import { MapMarkers } from '~/components/organisms/map-markers';
import { useWeeklyCommodityQueryResult, useWeldTypeId } from '~/hooks';
import { SearchBar } from '~/search-bar';
import { WeldFilterInput } from '~/search-bar/types';
import {
  CarouselContainerStyled,
  DashboardPersonaContentStyled,
  MapContainerStyled,
  WeldSectionListStyled,
} from '~/styled';
import { WeldTabs } from '~/weld-tabs';
import { CrewProgress } from './crew-progress';
import {
  SupDashboardLeftSectionStyled,
  SuperintendentDashboardStatStyled,
  SupWeldDashboardRightSectionStyled,
} from './styled';

interface SuperintendentDashboardContentProps {
  crewMember: CrewMember;
  enableProjectMap?: boolean;
  isDashboardSelectedUser: boolean;
}
interface NavigateToWeldProps {
  weldDrawerValuesProps: WeldDrawerValuesProps;
  isWeeklyStats?: boolean;
  applyBlockedFilter?: string;
}
export const SuperintendentDashboardContent: FC<SuperintendentDashboardContentProps> = ({
  crewMember,
  enableProjectMap,
  isDashboardSelectedUser,
}) => {
  const [showMap, setShowMap] = useState<boolean>(true);
  const navigate = useNavigate();
  const setDrawerValues = useSessionStorage<WeldDrawerValuesProps>(CacheKeys.WeldDrawerValues)[1];
  const setCursor = useSessionStorage<number>(CacheKeys.WeldListCursor)[1];
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isWideScreen = useMediaQuery(aboveBreakpoint(breakpoints.widescreen));
  const [weldFilter, setWeldFilter] = useState<WeldFilterInput>();
  const { features } = useConfig();
  const { weldTypeId } = useWeldTypeId();
  const { weeklyCommodityStatsError, weeklyCommodityStatsData, weeklyCommodityStatsLoading, weeklyRefetch } =
    useWeeklyCommodityQueryResult(weldTypeId, crewMember, weldFilter);
  const weeklyStatusStats: CommodityStats[] = get(
    weeklyCommodityStatsData?.commodityStats,
    'commodityStats.statusStats',
    []
  );
  const navigateToWeld = async (navigateToWeldProps: NavigateToWeldProps) => {
    setCursor(1);
    await setDrawerValues(navigateToWeldProps?.weldDrawerValuesProps);

    if (navigateToWeldProps?.isWeeklyStats || navigateToWeldProps?.applyBlockedFilter) {
      navigate('/', {
        state: {
          isWeeklyStats: navigateToWeldProps?.isWeeklyStats,
          applyBlockedFilter: navigateToWeldProps?.applyBlockedFilter,
        },
      });
    } else {
      navigate('/');
    }
  };
  const onSearch = (filter) => {
    setWeldFilter(filter);
  };
  return (
    <DashboardPersonaContentStyled key={crewMember?.id}>
      <SuperintendentDashboardStatStyled>
        <ErrorBoundary>
          <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
            <CommodityStatsSection
              personaType={PersonaType.SUPERINTENDENT}
              crewMember={crewMember}
              navigateToWeld={navigateToWeld}
              weldTypeId={weldTypeId}
              loading={weeklyCommodityStatsLoading}
              error={weeklyCommodityStatsError}
              refetch={weeklyRefetch}
              weeklyStatusStats={weeklyStatusStats}
              weldFilter={weldFilter}
            />
          </Suspense>
        </ErrorBoundary>
      </SuperintendentDashboardStatStyled>
      {isWideScreen ? (
        <WeldSectionListStyled key={crewMember?.id}>
          <SupDashboardLeftSectionStyled>
            {crewMember?.persona !== PersonaType.SUPER_ADMIN && (
              <SearchBar
                onSearch={onSearch}
                weldFilter={weldFilter}
                weldTypeId={weldTypeId}
                personaType={PersonaType.SUPERINTENDENT}
                crewMember={crewMember}
                showMap={showMap}
                toggleMap={setShowMap}
                enableProjectMap={enableProjectMap}
              />
            )}
            {showMap && enableProjectMap && (
              <MapContainerStyled key={crewMember?.id}>
                <MapMarkers weldFilter={weldFilter} crewMember={crewMember} />
              </MapContainerStyled>
            )}
            <WeldTabs
              personaType={PersonaType.SUPERINTENDENT}
              weldFilter={weldFilter}
              crewMember={crewMember}
              weldTypeId={weldTypeId}
            />
          </SupDashboardLeftSectionStyled>
          <SupWeldDashboardRightSectionStyled key={crewMember?.id}>
            <CrewProgress crewMember={crewMember} isDashboardSelectedUser={isDashboardSelectedUser}></CrewProgress>
            {features?.showShiftLog && (
              <Box mt={{ sm: '550' }}>
                <ShiftLog />
              </Box>
            )}
          </SupWeldDashboardRightSectionStyled>
        </WeldSectionListStyled>
      ) : (
        <Carousel key={crewMember?.id}>
          <CarouselContainerStyled>
            {crewMember?.persona !== PersonaType.SUPER_ADMIN && (
              <SearchBar
                onSearch={onSearch}
                weldFilter={weldFilter}
                weldTypeId={weldTypeId}
                personaType={PersonaType.SUPERINTENDENT}
                crewMember={crewMember}
                showMap={showMap}
                toggleMap={setShowMap}
                enableProjectMap={enableProjectMap}
              />
            )}
            <WeldTabs
              personaType={PersonaType.SUPERINTENDENT}
              weldFilter={weldFilter}
              crewMember={crewMember}
              weldTypeId={weldTypeId}
            />
          </CarouselContainerStyled>
          <CarouselContainerStyled>
            {crewMember?.persona !== PersonaType.SUPER_ADMIN && (
              <SearchBar
                onSearch={onSearch}
                weldFilter={weldFilter}
                weldTypeId={weldTypeId}
                personaType={PersonaType.SUPERINTENDENT}
                crewMember={crewMember}
                showMap={showMap}
                toggleMap={setShowMap}
                enableProjectMap={enableProjectMap}
              />
            )}
            {showMap && enableProjectMap && (
              <MapContainerStyled style={{ height: '100%' }} key={crewMember?.id}>
                <MapMarkers weldFilter={weldFilter} crewMember={crewMember} />
              </MapContainerStyled>
            )}
          </CarouselContainerStyled>
          <CrewProgress crewMember={crewMember} isDashboardSelectedUser={isDashboardSelectedUser}></CrewProgress>
          {features?.showShiftLog && <ShiftLog />}
        </Carousel>
      )}
    </DashboardPersonaContentStyled>
  );
};
